'use client'

import Link from 'next/link'
import {
  ChatCircle,
  GitBranch,
  Calendar,
  Books,
  Video,
  Brain,
  ChartBar,
  GearSix,
} from '@phosphor-icons/react'
import { useSettingsStore } from '@/store/settings-store'

const FEATURES = [
  {
    href: '/chat',
    label: 'Chat',
    icon: ChatCircle,
    description: 'Talk to agents and teams',
  },
  {
    href: '/genesis',
    label: 'Genesis',
    icon: GitBranch,
    description: 'Generate project documents',
  },
  {
    href: '/schedule',
    label: 'Schedule',
    icon: Calendar,
    description: 'AI-generated project schedules',
  },
  {
    href: '/knowledge',
    label: 'Knowledge',
    icon: Books,
    description: 'Search the knowledge base',
  },
  {
    href: '/meetings',
    label: 'Meetings',
    icon: Video,
    description: 'Bot joins and records meetings',
  },
  {
    href: '/memory',
    label: 'Memory',
    icon: Brain,
    description: 'Agent memory store',
  },
  {
    href: '/usage',
    label: 'Usage',
    icon: ChartBar,
    description: 'Token usage and costs',
  },
  {
    href: '/settings',
    label: 'Settings',
    icon: GearSix,
    description: 'Configure tenant and API keys',
  },
]

export default function HomePage() {
  const { clientId, projectId, userId } = useSettingsStore()

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="mx-auto max-w-3xl px-6 py-16 space-y-12">

        {/* Hero */}
        <div className="space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
            Welcome to Kael
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            AI-powered platform for construction project management — chat with agents,
            generate documents, schedule projects, and record meetings.
          </p>
        </div>

        {/* Active context */}
        {(clientId || projectId || userId) && (
          <div className="rounded-lg border border-border bg-card px-4 py-3 flex items-center gap-3 flex-wrap">
            <span className="text-xs text-muted-foreground shrink-0">Active context</span>
            {clientId && (
              <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs text-foreground">
                {clientId}
              </span>
            )}
            {projectId && (
              <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs text-foreground">
                {projectId}
              </span>
            )}
            {userId && (
              <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs text-foreground">
                {userId}
              </span>
            )}
            <Link
              href="/settings"
              className="ml-auto text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Edit in Settings →
            </Link>
          </div>
        )}

        {/* Feature grid */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {FEATURES.map(({ href, label, icon: Icon, description }) => (
            <Link
              key={href}
              href={href}
              className="group rounded-lg border border-border bg-card p-4 space-y-2 hover:bg-accent/50 hover:border-accent transition-colors"
            >
              <div className="flex items-center gap-2">
                <Icon
                  size={16}
                  weight="fill"
                  className="text-muted-foreground group-hover:text-foreground transition-colors"
                />
                <span className="text-sm font-medium text-foreground">{label}</span>
              </div>
              <p className="text-xs text-muted-foreground leading-snug">{description}</p>
            </Link>
          ))}
        </div>

      </div>
    </div>
  )
}
